import React, { useMemo } from "react";
import { isNullOrEmpty } from "../../utils/commonUtils";
import { cloneDeep } from "lodash";
import { ChevronDown,ChevronUp, Check, TriangleAlert } from "lucide-react";
import { CommentLoadSection, CircleLoader } from "../../components/ui/commentLoadSection";
import { Flex,FlexItem } from "../layout/FlexLayout";
import { Grid } from "../layout/GridLayout";
import { StatusBadge } from '../loan-list/column-formatter';


export const FileUploadProgress = ({info,index})=>{
const {isUploaded,fileName,fileSize, percentage} = info;
const loaderColors = {trackColor:"#FFF", color:"#DE7538"}

return <FlexItem key={index} className="flex-1">
  <Flex justify='between' alignItems="center" direction="column">
    <FlexItem><div className="text-xs text-neutral-13">{fileName}</div>
<div className="text-xs text-neutral-8">{fileSize? (fileSize/1048576).toFixed(2):'--'} MB</div>    
    </FlexItem>
    <FlexItem>
     {isUploaded && <StatusBadge size ='md' status={<p className='flex gap-2'><Check className='w-5 h-5' /> Uploaded</p>} variant="submitted" />}
     {!isUploaded && <Flex direction='column' items='center' gap={1}>
     <CircleLoader  percentage={percentage} size={20} strokeWidth={2} trackColor={loaderColors.trackColor} color={loaderColors.color} />
     <div className="text-sm pr-2 text-[var(--color-misc-progress-orange-2)]">{info.message}</div> 
     </Flex>} 
    </FlexItem>
  
  </Flex>
</FlexItem>
};

export const createDocumentResponseLocally = (
  files,
  localContext,
  contextKey,
  documentType,
  parentType,
  parentId
) => {
  return files.map((file, index) => {
    return {
      ...emptyValuedDocument,
      index: localContext?.[contextKey]?.length + index,
      name: file.name,
      createdOn: new Date().toISOString(),
      documentType,
      parentType,
      parentId
    };
  });
};

export const emptyValuedDocument = {
  documentId: null,
  documentType: "Others",
  parentType: "",
  parentId: "",
  name: "",
  path: "",
  review: {
    reason: null,
    reviewStatus: "Pending Review"
  },
  comment: null,
  type: "tag",
  status: "ACTIVE",
  fileSize: 0,
  statementDate: null
};


export const extensionList = [
  "docs",
  "docx",
  "doc",
  "pdf",
  "jpg",
  "jpeg",
  "png",
  "xlsx",
  "xls"
];

export const assignDocIdAndPath = (
  files,
  localContext,
  contextKey,
  documentType,
  parentType,
  parentId
) => {
  return files.map((file, index) => {
    return {
      ...emptyValuedDocument,
      index: localContext?.[contextKey]?.length + index,
      name: file.name,
      createdOn: file.createdOn,
      documentId: file.documentId,
      path: file.path,
      documentType,
      parentType,
      parentId
    };
  });
};

export const toUpperCase = (_extensionList) => {
  return _extensionList.map((item) => {
    return item.toUpperCase();
  });
};
const errorMessage = (list) => {
  const uppercaseExtensionList = toUpperCase(list);
  return `Supported file formats: ${uppercaseExtensionList
    .join(", ")
    .replace(/_/g, " ")}.`;
};

const checkDocFormat = (file, overRideFileFormat) => {
  const fileExt = file.name.split(".").pop().toLowerCase();
  const invalidFileFormat =
    (overRideFileFormat || extensionList).indexOf(fileExt) === -1;
  if (invalidFileFormat) {
    return errorMessage(overRideFileFormat || extensionList);
  }
  if (file.size > 524288000) {
    return "Max file fileSize: 500MB";
  }
  return "";
};

export const upload = ({
  idx,
  file,
  progressInfos,
  setProgressInfos,
  loanId,
  originatorId,
  originatorLoanId,
  overRideFileFormat,
  generatePreSignedUrl,
  uploadToAwsS3
  
}) => {
  const UPLOADING_FAILED = "Uploading failed";
  const clonedProgressInfos = cloneDeep(progressInfos);
  const fileValidation = checkDocFormat(file, overRideFileFormat);
  if (!isNullOrEmpty(fileValidation)) {
    setProgressInfos((prev) => {
      const updatedPrev = [...prev];
      updatedPrev[idx].percentage = 0;
      updatedPrev[idx].message = UPLOADING_FAILED;
      updatedPrev[idx].description = fileValidation;
      updatedPrev[idx].isUploaded = false;
      return updatedPrev;
    });
    return;
  }
  generatePreSignedUrl({
    path: `loan/${loanId}/${Date.now()}/${file?.name}`,
    originatorId,
    originatorLoanId
  })
    .then((resp) => {
      const preSignedUrl = resp?.data;
      clonedProgressInfos[idx].path = preSignedUrl.split("?")[0];
      uploadToAwsS3(preSignedUrl, file, (event) => {
        setProgressInfos((prev) => {
          const updatedPrev = [...prev];
          updatedPrev[idx].percentage = Math.round(
            (100 * event.loaded) / event.total
          );
          updatedPrev[idx].path = preSignedUrl.split("?")[0];
          return updatedPrev;
        });
      })
        .then(() => {
          setProgressInfos((prev) => {
            const updatedPrev = [...prev];
            updatedPrev[idx].message = "Uploaded successfully";
            updatedPrev[idx].isUploaded = true;
            return updatedPrev;
          });
        })
        .catch(() => {
          setProgressInfos((prev) => {
            const updatedPrev = [...prev];
            updatedPrev[idx].percentage = 0;
            updatedPrev[idx].message = UPLOADING_FAILED;
            updatedPrev[idx].isUploaded = false;
            return updatedPrev;
          });
        });
    })
    .catch(() => {
      setProgressInfos((prev) => {
        const updatedPrev = [...prev];
        updatedPrev[idx].percentage = 0;
        updatedPrev[idx].message = UPLOADING_FAILED;
        updatedPrev[idx].isUploaded = false;
        return updatedPrev;
      });
    });
};
export const validateFilesFormat = (docs,allowedExtension, setErrorMsg) => {
  let validations = false;
  validations = docs.some((file) => {
    const fileExt = file.name.split(".").pop().toLowerCase();
    const invalidFileFormat = allowedExtension.indexOf(fileExt) === -1;
    if (file.size > 524288000 || invalidFileFormat) {
      if (invalidFileFormat) {
        setErrorMsg?.("Wrong file format");
      } else {
        setErrorMsg?.("File size is more than 500MB");
      }
      return true;
    }
    setErrorMsg?.("");
  });

  return validations;
};

export const LoaderSection = ({ progressInfos=[]})=>{
    const loaderColors = {trackColor:"#DE7538", color:"#FFF"}
    const {isUploading,errorCount,isError,uploadingCount,totalFiles} = useMemo(()=>{
      let isUploading = false;
      let uploadingCount = 0;
      let isError = false;
      let errorCount = 0;
      progressInfos.forEach((info)=>{
        if (info.isUploaded === null || info.isUploaded === false){
            if(info.message === 'Uploading failed'){
                errorCount++;
            }
            else {
                isUploading = true;
                uploadingCount++;
            }
        }
      });
      if(isUploading === false){
        isError = errorCount > 0;
      }

      return {isUploading,uploadingCount,totalFiles:progressInfos.length,isError,errorCount};
    },[progressInfos])

    let expandDetails= <p className="flex gap-2">  <span>Collapse Details</span> <ChevronUp /></p>;
    let collapsedDetails= <p className="flex gap-2"><span>Expand Details </span><ChevronDown /></p>;


    if (!isUploading && isError){
        loaderColors.trackColor="#DA1E28";
        loaderColors.color="#DA1E28";
        expandDetails= <p className="flex gap-2 items-center"> <TriangleAlert className='text-system-negative-3' /> <span>Collapse Details</span> <ChevronUp /></p>;
        collapsedDetails= <p className="flex gap-2 items-center"><TriangleAlert className='text-system-negative-3' /><span>Expand Details </span><ChevronDown /></p>;

    }
    

    if (progressInfos.length === 0) {
      return null;
    }

    const percentageLoad = isUploading ? 100*uploadingCount/totalFiles : 100;
    return   <div className="pt-4">
    <CommentLoadSection>
    <CommentLoadSection.Header isError={isError} className="mb-4">
    <Flex alignItems="center" gap="3" justify="between" className="mb-2" direction="row">
        <FlexItem>
        <Flex gap='2'>
            <FlexItem className="w-14 h-13">
                <CircleLoader percentage={percentageLoad} size={50} strokeWidth={3} trackColor={loaderColors.trackColor} color={loaderColors.color} showFileIcon />
            </FlexItem>
            {isUploading || errorCount===0 ?<Flex direction="col" gap="1" items='start' >

                {
                uploadingCount >0 ?<>
                    <h3 className=" text-md font-semibold text-neutral-8">{uploadingCount} {uploadingCount>1?'Files':'File'} are uploading</h3>
                    <p>Please wait while the upload completes. Your documents will be automatically sorted under the appropriate sections.</p>
                    </>:
                    <>
                    <h3 className=" text-md font-semibold text-neutral-8">Files uploaded succesfully</h3>
                    <p>Your documents will be automatically sorted under the appropriate sections.</p> 
                    </> 
                }               
            </Flex>:
            <Flex direction="col" gap="1" items='start' >
                <h3 className=" text-md font-semibold text-neutral-8">{errorCount} {errorCount>1?'Errors':'Error'} while uploading</h3>
                <p>Some files failed to upload. Please check the details to upload the correct ones or try again.</p>
            </Flex>
            }
            
        </Flex>
        </FlexItem>
        <Flex items="center">
            <CommentLoadSection.HeaderAction expandedText={expandDetails} collapsedText={collapsedDetails} isError={isError} />
        </Flex>
    </Flex>
    </CommentLoadSection.Header>
    <CommentLoadSection.Content className="mb-4">
    <Flex alignItems="center" gap="3" className="p-4 bg-white" >
        <Grid cols={2} gap={4} className="w-full">
        {progressInfos.map((info) => (
        <FileUploadProgress key={info.name} info={info} />
        ))}
        </Grid>
    </Flex>
    </CommentLoadSection.Content>
    </CommentLoadSection>
    </div>
}
